import React from 'react';
import dynamic from 'next/dynamic';
import { getLog } from '@aurora/shared-utils/log';
import type { Attachment } from '@aurora/shared-generated/types/graphql-schema-types';
import type { ItemType } from '../../../types/enums';
import { AttachmentViewVariant } from '../../../types/enums';
import type AttachmentOption from '@aurora/shared-client/helpers/attachment/AttachmentOption';
import type { ItemViewFC, ItemViewVariantFC } from '../../entities/types';

const log = getLog(module);

const AttachmentViewChip = dynamic(() => import('./AttachmentViewChip/AttachmentViewChip'));

const variantToComponentMap: Record<
  AttachmentViewVariant,
  ItemViewVariantFC<Attachment | AttachmentOption, ItemType.ATTACHMENT, AttachmentViewVariant>
> = {
  chip: AttachmentViewChip
};

/**
 * The base Attachment view component that renders the Attachment view specified by
 * the <code>variant</code> parameter. This component should be used anywhere a Attachment is rendered.
 *
 * @constructor
 * @author Dolan Halbrook
 */
const AttachmentView: ItemViewFC<
  Attachment | AttachmentOption,
  ItemType.ATTACHMENT,
  AttachmentViewVariant
> = ({ entity: attachment, variant = { type: AttachmentViewVariant.CHIP }, className }) => {
  const AttachmentViewComponent = variantToComponentMap[variant?.type];

  if (AttachmentViewComponent) {
    // eslint-disable-next-line react/jsx-props-no-spreading
    return <AttachmentViewComponent entity={attachment} className={className} {...variant.props} />;
  }
  log.warn('No registered Attachment view for variant type [%s]', variant.type);
  return null;
};

export default AttachmentView;
